import { Popup, Typography } from '@tunasong/ui-lib'
import { formatNumber } from '@tunasong/models'
import { useDragMove } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, HTMLAttributes, Ref } from 'react'
import { useCallback, useState } from 'react'
import styles from './channel-pan.module.css'

const getLeftWidth = (pan = 0) => `${pan < 0 ? Math.abs(pan) * 100 : 0}%`
const getRightWidth = (pan = 0) => `${pan > 0 ? Math.abs(pan) * 100 : 0}%`
const getLabel = (pan = 0) => `${formatNumber(Math.abs(pan) * 100, { digits: 1 })} ${pan < 0 ? 'L' : 'R'}`

export interface ChannelPanProps {
  className?: string
  /** From -1 to 1, 0 is center */
  pan?: number
  onPan(pan: number): void
}

const PanContainer = ({ ref, ...props }: HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> }) => (
  <div ref={ref} {...props} className={cn(styles.panContainer, props.className)} style={props.style} />
)

const CenterContainer = (props: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cn(styles.centerContainer, props.className)} style={props.style} />
)

const LeftContainer = (props: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cn(styles.leftContainer, props.className)} style={props.style} />
)

const RightContainer = (props: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cn(styles.rightContainer, props.className)} style={props.style} />
)

interface PanBarProps extends HTMLAttributes<HTMLDivElement> {
  panValue?: number
}

const LeftPanBar = ({ panValue = 0, ...props }: PanBarProps) => (
  <div
    {...props}
    className={cn(styles.panBar, props.className)}
    style={{ width: getLeftWidth(panValue), ...props.style }}
  />
)

const RightPanBar = ({ panValue = 0, ...props }: PanBarProps) => (
  <div
    {...props}
    className={cn(styles.panBar, props.className)}
    style={{ width: getRightWidth(panValue), ...props.style }}
  />
)

export const ChannelPan: FC<ChannelPanProps> = props => {
  const { className, pan = 0, onPan } = props

  /** Dragging the volume bar? */
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)

  const handlePan = useCallback(
    (ev: MouseEvent) => {
      if (!anchorEl) {
        return
      }
      ev.preventDefault()
      const box = anchorEl.getBoundingClientRect()
      const pan = Math.max(0, Math.min(((ev.clientX - box.left) / box.width) * 2, 2)) - 1
      onPan(pan)
    },
    [onPan, anchorEl]
  )

  const handleStart = useCallback(
    (ev: MouseEvent) => {
      handlePan(ev)
      setShowPan(true)
    },
    [handlePan]
  )

  const handleStop = useCallback(
    (ev: MouseEvent) => {
      handlePan(ev)
      setShowPan(false)
    },
    [handlePan]
  )

  const [showPan, setShowPan] = useState(false)

  useDragMove({
    el: anchorEl,
    onMove: handlePan,
    onStart: handleStart,
    onStop: handleStop,
  })

  return (
    <PanContainer className={className} title={getLabel(pan)} ref={setAnchorEl}>
      <LeftContainer>
        <LeftPanBar panValue={pan} />
      </LeftContainer>
      <CenterContainer />
      <RightContainer>
        <RightPanBar panValue={pan} />
      </RightContainer>
      <Popup open={showPan} anchorEl={anchorEl} placement="top">
        <Typography variant="caption">{getLabel(pan)}</Typography>
      </Popup>
    </PanContainer>
  )
}

export default ChannelPan
